// Personality, constants, computed helpers

export const EMOJIS = [
  "🎣", "🐟", "🐠", "🦈", "🐡", "🦞", "🦀", "🐙", "🐳", "🐬",
  "🐋", "🐢", "🦆", "🧔", "👨", "👩", "👴", "👵", "🧒", "👦",
  "👧", "🤠", "😎", "🕶️", "🐊",
];

export const COLORS = [
  "#E8A838", "#0A3D62", "#2D6A4F", "#D9381E", "#8E44AD",
  "#16A085", "#3498DB", "#F39C12", "#9B59B6", "#1ABC9C",
  "#34495E", "#E67E22",
];

export const SPECIES = ["Bass", "Yellowbelly", "Cod", "Catfish", "Other"];

export const LOADING_LINES = [
  "Reeling it in…",
  "Weighing the bragger…",
  "Updating the family group chat…",
  "Consulting the fish gods…",
];

export const KISSED_THE_WATER_LINES = [
  "No fish yet 😅 the dam commiserates",
  "Zero. But the day is young.",
  "Perhaps try bait?",
  "Even a stopped clock catches something.",
  "The fish know. They always know.",
];

export const PIN_TITLE = "Family PIN, please 🎣";
export const PIN_WRONG = "Nope. Try again.";
export const EMPTY_LEADERBOARD = "No fish yet. The dam is full of them. Go.";
export const EMPTY_FEED = "Nothing here. Suspicious.";

export function fishReaction(cm) {
  if (cm >= 60) return { label: "MONSTER 🚨", color: "#D9381E" };
  if (cm >= 45) return { label: "Solid unit", color: "#E8A838" };
  if (cm >= 30) return { label: "Respectable", color: "#2D6A4F" };
  if (cm >= 20) return { label: "Alright", color: "#556B7D" };
  return { label: "Cute", color: "#556B7D" };
}

export function computeLeaders(catches, members) {
  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));
  const stats = {}; // id -> { count, biggest, smallest }
  members.forEach((m) => {
    stats[m.id] = { count: 0, biggest: null, smallest: null };
  });
  catches.forEach((c) => {
    const s = stats[c.catch_owner_id];
    if (!s) return;
    s.count += 1;
    if (s.biggest == null || c.length_cm > s.biggest) s.biggest = c.length_cm;
    if (s.smallest == null || c.length_cm < s.smallest) s.smallest = c.length_cm;
  });

  // Biggest catch overall — from raw catches (avoid ties problem: latest wins)
  let biggestCatch = null;
  let smallestCatch = null;
  catches.forEach((c) => {
    if (!biggestCatch || c.length_cm > biggestCatch.length_cm) biggestCatch = c;
    if (!smallestCatch || c.length_cm < smallestCatch.length_cm) smallestCatch = c;
  });

  // Most caught — by count, tie-break: whoever hit that count first (earliest catch)
  const anglersWithCatches = members
    .filter((m) => stats[m.id].count > 0)
    .map((m) => ({
      member: m,
      count: stats[m.id].count,
      biggest: stats[m.id].biggest,
    }));

  anglersWithCatches.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return 0;
  });

  const biggest = biggestCatch
    ? { member: memberById[biggestCatch.catch_owner_id], cm: biggestCatch.length_cm }
    : null;
  const smallest = smallestCatch
    ? { member: memberById[smallestCatch.catch_owner_id], cm: smallestCatch.length_cm }
    : null;
  const mostCaught = anglersWithCatches[0]
    ? { member: anglersWithCatches[0].member, count: anglersWithCatches[0].count }
    : null;

  return {
    biggest,
    smallest,
    mostCaught,
    stats,
    standingsByCount: anglersWithCatches,
    topBiggest: [...catches]
      .sort((a, b) => b.length_cm - a.length_cm)
      .slice(0, 5)
      .map((c) => ({ catch: c, member: memberById[c.catch_owner_id] })),
    anglersWithoutCatches: members.filter((m) => stats[m.id].count === 0),
  };
}

// Given previous & new leaders, compute a fun toast line
export function catchSaveToast({ member, cm, prev, next, wasFirst }) {
  if (!member) return { title: "Fish logged.", desc: "" };
  const name = member.name;

  // Trophy changes
  const biggestChanged =
    next.biggest && (!prev.biggest || prev.biggest.member?.id !== next.biggest.member?.id) &&
    next.biggest.member?.id === member.id;
  const smallestChanged =
    next.smallest && (!prev.smallest || prev.smallest.member?.id !== next.smallest.member?.id) &&
    next.smallest.member?.id === member.id;
  const mostCaughtChanged =
    next.mostCaught && (!prev.mostCaught || prev.mostCaught.member?.id !== next.mostCaught.member?.id) &&
    next.mostCaught.member?.id === member.id;

  if (biggestChanged) {
    const old = prev.biggest?.member?.name || "the field";
    return {
      title: `🏆 ${name} just yanked the Biggest crown off ${old}.`,
      desc: "Ruthless.",
    };
  }
  if (smallestChanged) {
    return {
      title: `🐟 ${name}'s ${cm.toFixed(0)}cm minnow claims the Smallest.`,
      desc: "Proud of you.",
    };
  }
  if (mostCaughtChanged) {
    return {
      title: `🎣 ${name} pulls into first for Most Caught.`,
      desc: "Someone check their bait.",
    };
  }
  if (wasFirst) {
    return { title: `${name} on the board.`, desc: "About time." };
  }
  if (cm >= 40) return { title: "Solid unit.", desc: `${name} landed ${cm.toFixed(0)}cm.` };
  return { title: "Add it to the pile.", desc: `${name}: ${cm.toFixed(0)}cm.` };
}

export function trophyGap(kind, leaderVal, secondVal) {
  if (leaderVal == null) return null;
  if (kind === "biggest") {
    if (secondVal == null) return "Uncontested. For now.";
    const gap = leaderVal - secondVal;
    if (gap <= 0) return "Tied at the top. Chaos.";
    if (gap >= 15) return "Absolute domination. Someone challenge them.";
    if (gap >= 5) return `${gap.toFixed(0)}cm clear. Comfortable.`;
    return "Wafer thin. One good cast and it's gone.";
  }
  if (kind === "smallest") {
    if (secondVal == null) return "The wooden crown, all alone.";
    return "Small but proud.";
  }
  if (kind === "mostCaught") {
    if (secondVal == null) return "Untouched. Fishing while others sleep.";
    const gap = leaderVal - secondVal;
    if (gap <= 0) return "Neck and neck. Get the net.";
    if (gap >= 5) return `${gap} ahead. Ridiculous.`;
    if (gap >= 2) return `${gap} clear. Nervous yet?`;
    return "Neck and neck. Get the net.";
  }
  return null;
}

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  target.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  const diff = Math.round((target - now) / (1000 * 60 * 60 * 24));
  return diff;
}

export function compStatus(start, end) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s = new Date(start);
  const e = new Date(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  if (today < s) {
    const days = Math.round((s - today) / (1000 * 60 * 60 * 24));
    return { label: `${days} day${days === 1 ? "" : "s"} until launch`, active: false };
  }
  if (today > e) return { label: "Comp closed", active: false };
  const days = Math.round((e - today) / (1000 * 60 * 60 * 24));
  return {
    label: days === 0 ? "Final day — send it" : `${days} day${days === 1 ? "" : "s"} left`,
    active: true,
  };
}

// Pre-fill: next Friday to next Monday
export function defaultTournamentDates() {
  const today = new Date();
  const day = today.getDay(); // 0 Sun ... 6 Sat
  // Days until next Friday (5)
  let daysToFri = (5 - day + 7) % 7;
  if (daysToFri === 0) daysToFri = 7;
  const fri = new Date(today);
  fri.setDate(today.getDate() + daysToFri);
  const mon = new Date(fri);
  mon.setDate(fri.getDate() + 3);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(fri), end: fmt(mon) };
}

export function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}
